import { hcl } from "d3-color";
import { interpolateHcl } from "d3-interpolate";
import {
  CRITICAL_RISK_SCORE_THRESHOLD,
  HIGH_RISK_SCORE_THRESHOLD,
  MEDIUM_RISK_SCORE_THRESHOLD,
  REVIEW_STATUS_OPTIONS,
} from "oso-for-agents/constants";
import {
  Agent,
  AlertTriggerConfig,
  AgentsAlertTrigger,
  IntegrationsAlertTrigger,
} from "oso-for-agents/types";

/**
 * Format a UTC timestamp string to the user's local timezone
 * @param timestamp - ISO 8601 timestamp string (UTC)
 * @returns Formatted date and time string (e.g., "Jan 30, 10:45 AM")
 */
export function formatTimestamp(timestamp: string | null | undefined): string {
  if (!timestamp) return "N/A";

  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "Invalid date";

    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    return "Invalid date";
  }
}

/**
 * Format a count for display, capping at 99+
 * @param count - The count to format
 * @returns Formatted count string (e.g., "5" or "99+")
 */
export function formatCount(count: number): string {
  return count > 99 ? "99+" : count.toString();
}

/**
 * Format a UTC timestamp string as relative time (e.g., "2m ago", "1h ago", "3d ago")
 * Falls back to absolute date format after 7 days
 * Clamps future timestamps to "just now"
 * @param timestamp - ISO 8601 timestamp string (UTC)
 * @returns Relative time string or absolute date
 */
export function formatRelativeTime(
  timestamp: string | null | undefined
): string {
  if (!timestamp) return "N/A";

  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "Invalid date";

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    if (diffMs < 0) return "just now";

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) return "just now";
    if (diffMinutes < 60) return `${diffMinutes}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;

    // After 7 days, show absolute date; include year if not in the current year.
    const dateOptions: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
    };

    if (date.getFullYear() !== now.getFullYear()) {
      dateOptions.year = "numeric";
    }

    return date.toLocaleDateString("en-US", dateOptions);
  } catch (error) {
    return "Invalid date";
  }
}

// Color interpolators from desaturated to saturated
const criticalInterpolator = interpolateHcl("#f1817a", "#fc0033");
const highInterpolator = interpolateHcl("#f8ac77", "#ff6e00");
const mediumInterpolator = interpolateHcl("#faf0a5", "#efdb00");
const lowInterpolator = interpolateHcl("#a0c795", "#00ac00");

function getSeverityColorInterpolator(severity: string) {
  switch (severity.toLowerCase()) {
    case "critical":
      return criticalInterpolator;
    case "high":
      return highInterpolator;
    case "medium":
      return mediumInterpolator;
    case "low":
      return lowInterpolator;
    default:
      console.error(`Unknown severity: ${severity}`);
      return lowInterpolator;
  }
}

export function getSeverityColor(severity: string, normalizedValue?: number) {
  const interpolator = getSeverityColorInterpolator(severity);

  const color = interpolator(normalizedValue ?? 1);
  const lch = hcl(color);
  return {
    backgroundColor: lch.formatHex(),
    textColor: lch.l < 60 ? "white" : "black",
  };
}

export function getRiskColorInterpolate(
  riskScore: number,
  usageCount?: number,
  minUsage?: number,
  maxUsage?: number
): { backgroundColor: string; textColor: string } {
  let severity = "low";
  if (riskScore >= CRITICAL_RISK_SCORE_THRESHOLD) severity = "critical";
  else if (riskScore >= HIGH_RISK_SCORE_THRESHOLD) severity = "high";
  else if (riskScore >= MEDIUM_RISK_SCORE_THRESHOLD) severity = "medium";

  // Normalize usageCount to [0, 1] range for interpolation
  // interpolateHcl expects a value between 0 (first color) and 1 (second color)
  let normalizedUsage = 1;
  if (
    usageCount !== undefined &&
    minUsage !== undefined &&
    maxUsage !== undefined
  ) {
    normalizedUsage =
      minUsage === maxUsage
        ? 1
        : (usageCount - minUsage) / (maxUsage - minUsage);
  }
  return getSeverityColor(severity, normalizedUsage);
}

/**
 * Dedup agents by displayName for use in filter dropdowns.
 */
export function getUniqueAgentsByDisplayName(
  agents: Agent[]
): { id: string; displayName: string }[] {
  return Array.from(
    new Map(
      agents.map((agent) => [
        agent.displayName,
        { id: agent.displayName, displayName: agent.displayName },
      ])
    ).values()
  );
}

/**
 * Aggregate agents by displayName, merging tools, risk, and lastSeen
 * across agent versions (e.g., claude-cli/1.0.1 + claude-cli/1.0.2 -> Claude Code).
 */
export function aggregateAgentsByDisplayName(agents: Agent[]): Agent[] {
  const aggregated = new Map<
    string,
    { displayName: string; tools: Set<string>; lastSeen: string; risk: number }
  >();

  for (const agent of agents) {
    if (aggregated.has(agent.displayName)) {
      const existing = aggregated.get(agent.displayName)!;
      for (const tool of agent.tools) {
        existing.tools.add(tool);
      }
      if (agent.lastSeen > existing.lastSeen) {
        existing.lastSeen = agent.lastSeen;
      }
      if (agent.risk > existing.risk) {
        existing.risk = agent.risk;
      }
    } else {
      aggregated.set(agent.displayName, {
        displayName: agent.displayName,
        tools: new Set(agent.tools),
        lastSeen: agent.lastSeen,
        risk: agent.risk,
      });
    }
  }

  return Array.from(aggregated.values()).map((agg) => ({
    id: agg.displayName,
    displayName: agg.displayName,
    tools: Array.from(agg.tools),
    lastSeen: agg.lastSeen,
    risk: agg.risk,
  }));
}

/**
 * Get the display label for a review status value
 * @param status - The review status value (e.g., "allowed", "unreviewed", "disallowed")
 * @returns Display label (e.g., "Allowed", "Unreviewed", "Disallowed")
 */
export function getReviewStatusLabel(status: string): string {
  return REVIEW_STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
}

/**
 * Get the description for an integration alert trigger
 * @param reviewStatus - The review status value (e.g., "disallowed", "unreviewed")
 * @returns Description string (e.g., "Disallowed tool used")
 */
export function getIntegrationTriggerDescription(reviewStatus: string): string {
  return `${getReviewStatusLabel(reviewStatus)} tool used`;
}

/**
 * Get a human-readable description for an alert trigger
 * @param config - The alert trigger configuration
 * @param patternName - Optional pattern name for content triggers
 * @returns Description string (e.g., "Flagged tool used" or "Pattern X detected")
 */
export function getTriggerDescription(
  config: AlertTriggerConfig,
  patternName?: string
): string {
  if (config.source === "integrations") {
    const c = config as IntegrationsAlertTrigger;
    return getIntegrationTriggerDescription(c.value);
  }
  if (config.source === "agents") {
    const c = config as AgentsAlertTrigger;
    return `${getReviewStatusLabel(c.reviewStatus)} agent detected`;
  }
  if (patternName) {
    return `${patternName} detected`;
  }
  return `Content pattern detected`;
}

/**
 * Parse a message for <system-reminder> tags and separate user content from system content.
 * @param text - The raw message text
 * @returns Object with userContent (text outside system-reminder tags) and systemContent (array of system-reminder contents)
 */
export function parseSystemContent(text: string): {
  userContent: string;
  systemContent: string[];
} {
  const systemContent: string[] = [];
  const regex = /<system-reminder>([\s\S]*?)<\/system-reminder>/g;

  let match;
  while ((match = regex.exec(text)) !== null) {
    systemContent.push(match[1].trim());
  }

  const userContent = text.replace(regex, "").trim();

  return { userContent, systemContent };
}
